import { modelSaid, type TeamPreset, type Thinking, thinkingFor, whatIsWrongSoFar } from "@rally/agreed";
import {
  Alert,
  Button,
  Field,
  Input,
  ListEmpty,
  ListSection,
  PageHeader,
  Row,
  ScrollArea,
  Select,
  SkeletonRows,
  Stack,
  Surface,
  Switch,
  Text,
  Toast,
  useConfirm,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { failureSaid } from "../localization/failure.ts";
import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";
import { goTo } from "../router.ts";
import { modelsOnOffer } from "./a-provider.ts";
import {
  type AgentConnection,
  agentConnections,
  connectionPayload,
  connectionStatus,
} from "./agent-connections.ts";
import { type Agent, AgentRow } from "./an-agent-row.tsx";
import { BriefingField } from "./briefing-field.tsx";
import { calledBy } from "./called.ts";
import { A_FAIR_EFFORT, aFairEffortIn } from "./effort.ts";
import { Effort } from "./effort.tsx";
import type { HeldSearchKey } from "./held.ts";

function saidWhy(cause: unknown): string {
  return failureSaid(cause, say("head.broke"));
}

const UNDO_MS = 8000;

export function Agents({ me, runsIt = false }: { me?: string; runsIt?: boolean }) {
  const _theme = useTheme();
  const [mine, setMine] = useState<Agent[] | null>(null);

  const [providers, setProviders] = useState<AgentConnection[] | null>(null);

  const [searchKeys, setSearchKeys] = useState<HeldSearchKey[]>([]);
  const [wrong, setWrong] = useState<string | null>(null);
  const [gone, setGone] = useState<{ account: string; name: string } | null>(null);
  const goneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { ask, dialog } = useConfirm();
  const [making, setMaking] = useState(false);
  const [busy, setBusy] = useState(false);

  const [handle, setHandle] = useState("");
  const [credential, setCredential] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [thinking, setThinking] = useState<Thinking>(A_FAIR_EFFORT);
  const [tools, setTools] = useState(false);
  const [definition, setDefinition] = useState("");

  const again = useCallback(() => {
    read<Agent[]>("my.agents")
      .then(setMine)
      .catch((cause: unknown) => {
        setMine([]);
        setWrong(saidWhy(cause));
      });
  }, []);

  const keys = useCallback(() => {
    void read<HeldSearchKey[]>("search.keys")
      .then(setSearchKeys)

      .catch(() => setSearchKeys([]));
    void agentConnections()
      .then((said) => {
        setProviders(said);
        const first = said.find((one) => !one.broker);
        if (first) {
          setCredential((was) => was || first.id);
          const offered = modelsOnOffer(first)[0];
          if (offered) {
            setModel((was) => was || offered);
            setThinking(aFairEffortIn(thinkingFor(offered, first.provider)));
          }
        }
      })
      .catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    keys();
    again();
  }, [again, keys]);

  const chosen = providers?.find((one) => one.id === credential);
  const models = chosen ? modelsOnOffer(chosen) : [];
  const levels = model && chosen ? thinkingFor(model, chosen.provider) : [];
  const wrongHandle = whatIsWrongSoFar(handle);
  const ready = handle.length >= 2 && !wrongHandle && Boolean(model) && Boolean(credential);

  async function _startTheTeam(preset: TeamPreset): Promise<void> {
    setBusy(true);
    setWrong(null);
    try {
      for (const member of preset.members) {
        await act("agent.create", {
          handle: member.handle,
          model,
          ...connectionPayload(chosen, credential),
          thinking,
          tools: true,
          capabilities: member.capabilities ? member.capabilities.split(",") : [],
          definition: member.says,
        });
      }
      again();
    } catch (cause) {
      setWrong(saidWhy(cause));
    } finally {
      setBusy(false);
    }
  }

  async function make(): Promise<void> {
    setBusy(true);
    setWrong(null);
    try {
      await act("agent.create", {
        handle,
        model,
        ...connectionPayload(chosen, credential),
        thinking,
        tools,

        ...(definition.trim() ? { definition } : {}),
      });
      setHandle("");
      setDefinition("");
      setMaking(false);
      again();
    } catch (cause) {
      setWrong(saidWhy(cause));
    } finally {
      setBusy(false);
    }
  }

  async function shareWithEveryone(agent: Agent): Promise<void> {
    setWrong(null);
    try {
      const shared = agent.shares.some((one) => one.scope === "server");
      if (shared) await act("agent.unshare", { account: agent.account, scope: "server" });
      else await act("agent.share", { account: agent.account, scope: "server" });
      again();
    } catch (cause) {
      setWrong(saidWhy(cause));
    }
  }

  async function unmake(agent: Agent): Promise<void> {
    const sure = await ask({
      title: say("agents.removeTitle")(calledBy(agent) ?? agent.handle),
      description: say("agents.removeMeans"),
      confirmLabel: say("agents.remove"),
      cancelLabel: say("agents.keep"),
      tone: "danger",
    });
    if (!sure) return;
    setWrong(null);
    try {
      await act("agent.remove", { account: agent.account });
      again();
      setGone({ account: agent.account, name: calledBy(agent) ?? agent.handle });
      if (goneTimer.current) clearTimeout(goneTimer.current);
      goneTimer.current = setTimeout(() => setGone(null), UNDO_MS);
    } catch (cause) {
      setWrong(saidWhy(cause));
    }
  }

  async function bringBack(): Promise<void> {
    const it = gone;
    if (!it) return;
    setGone(null);
    if (goneTimer.current) clearTimeout(goneTimer.current);
    try {
      await act("agent.restore", { account: it.account });
      again();
    } catch (cause) {
      setWrong(saidWhy(cause));
    }
  }

  const groups = (
    [
      [say("agents.mine"), (agent: Agent) => me !== undefined && agent.owner === me],
      [
        say("agents.sharedWithYou"),
        (agent: Agent) => agent.kind !== "built_in" && !(me !== undefined && agent.owner === me),
      ],
      [say("agents.builtIn"), (agent: Agent) => agent.kind === "built_in"],
    ] as const
  )
    .map(([title, holds]) => ({
      title,
      agents: (mine ?? [])
        .filter(holds)
        .sort((a, b) => (calledBy(a) ?? a.handle).localeCompare(calledBy(b) ?? b.handle)),
    }))
    .filter((group) => group.agents.length > 0);

  const total = (mine ?? []).length;

  return (
    <Stack gap={4}>
      {dialog}
      <PageHeader
        title={say("you.agents")}
        {...(mine !== null && total > 0 ? { count: total } : {})}
        action={
          total === 0 || making || (providers !== null && providers.length === 0) ? null : (
            <Button size="sm" icon="plus" testID="agents-new" onPress={() => setMaking(true)}>
              {say("agents.make")}
            </Button>
          )
        }
      />

      <Stack gap={3}>
        {wrong ? <Alert tone="danger">{wrong}</Alert> : null}
        {gone ? (
          <Toast
            tone="neutral"
            icon="trash"
            action={{ label: say("agents.undo"), onPress: () => void bringBack(), testID: "agents-undo" }}
          >
            {say("agents.removed")(gone.name)}
          </Toast>
        ) : null}

        {mine === null ? (
          <SkeletonRows rows={3} face={36} />
        ) : mine.length === 0 ? (
          <ListEmpty
            icon="bot"
            line={say("agents.none")}
            action={
              providers !== null && providers.length > 0 ? (
                <Button icon="plus" onPress={() => setMaking(true)} testID="agent-make-empty">
                  {say("agents.make")}
                </Button>
              ) : undefined
            }
          />
        ) : groups.length === 0 ? (
          <ListEmpty icon="search" line={say("agents.noneFound")} />
        ) : (
          groups.map((group) => (
            <ListSection key={group.title} {...(groups.length > 1 ? { title: group.title } : {})}>
              {group.agents.map((agent, index, all) => (
                <AgentRow
                  key={agent.account}
                  agent={agent}
                  searchKeys={searchKeys.length}
                  yours={me !== undefined && agent.owner === me}
                  runsIt={runsIt}
                  last={index === all.length - 1}
                  onChanged={again}
                  onShare={() => void shareWithEveryone(agent)}
                  onRemove={() => void unmake(agent)}
                />
              ))}
            </ListSection>
          ))
        )}

        {providers !== null && providers.length === 0 ? (
          <Stack gap={2}>
            <Text typeName="body" color="text.tertiary">
              {say("agents.noProviderYet")}
            </Text>
            <Row>
              <Button
                variant="soft"
                icon="key"
                onPress={() => goTo("/me/integrations")}
                testID="agents-to-providers"
              >
                {say("you.integrations")}
              </Button>
            </Row>
          </Stack>
        ) : null}
        <Surface
          kind="panel"
          width={520}
          visible={making}
          onDismiss={() => setMaking(false)}
          title={say("agents.make")}
          testID="agent-create-panel"
          footer={
            <Row gap={2}>
              <Button loading={busy} disabled={!ready} onPress={() => void make()} testID="agent-create">
                {say("agents.create")}
              </Button>
              <Button variant="ghost" onPress={() => setMaking(false)}>
                {say("providers.cancel")}
              </Button>
            </Row>
          }
        >
          <ScrollArea contentContainerStyle={{ padding: 20 }}>
            <Stack gap={4}>
              <Field
                label={say("agents.handle")}
                hint={say("agents.handleHint")}
                error={wrongHandle ?? undefined}
              >
                <Input value={handle} onChangeText={setHandle} autoFocus testID="agent-handle" />
              </Field>
              <Field label={say("agents.provider")}>
                <Select
                  testID="agent-create-connection"
                  value={credential}
                  onChange={(id) => {
                    setCredential(id);
                    const picked = providers?.find((one) => one.id === id);
                    const first = picked ? modelsOnOffer(picked)[0] : undefined;
                    setModel(first ?? "");
                    if (first && picked) setThinking(aFairEffortIn(thinkingFor(first, picked.provider)));
                  }}
                  options={(providers ?? []).map((one) => ({ label: one.label, value: one.id }))}
                />
              </Field>
              {connectionStatus(chosen, model) ? (
                <Text typeName="caption" color="text.secondary" testID="agent-broker-status">
                  {connectionStatus(chosen, model)}
                </Text>
              ) : null}
              <Field label={say("agents.model")}>
                <Select
                  value={model}
                  onChange={(id) => {
                    setModel(id);
                    if (chosen) setThinking(aFairEffortIn(thinkingFor(id, chosen.provider)));
                  }}
                  options={models.map((one) => ({ label: modelSaid(one), value: one }))}
                />
              </Field>
              <Effort offered={levels} value={thinking} onChange={setThinking} />
              <Field label={say("agents.tools")} hint={say("agents.toolsHint")}>
                <Switch value={tools} onChange={setTools} />
              </Field>
              <BriefingField value={definition} onChange={setDefinition} />
            </Stack>
          </ScrollArea>
        </Surface>
      </Stack>
    </Stack>
  );
}
